import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Save } from 'lucide-react'
import toast from 'react-hot-toast'
import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { useAuthStore } from '@/store/auth.store'
import { trainingService } from '@/services/trainingService'
import { employeesService } from '@/services/employeesService'
import type { TrainingVm } from '@/api/types/training.types'
import { showError, showSuccess } from '@/lib/swal'

export const TrainingEdit: React.FC = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const { user } = useAuthStore()
  const branchId = user?.selectedBranch?.branchId
  const isEditing = Boolean(id && id !== 'new')

  const [loading, setLoading] = useState(false)
  const [employees, setEmployees] = useState<{ id?: number; empCode?: string; empFirstNameEn?: string; empSecondNameEn?: string; empLastNameEn?: string }[]>([])

  const [form, setForm] = useState({
    empId: 0,
    courseTitle: '',
    trainingDonor: '',
    trainingYear: '' as string | number,
    description: '',
  })

  useEffect(() => {
    if (!branchId) return
    const loadEmployees = async () => {
      try {
        const res = await employeesService.getAllEmployees(branchId, 1, 500)
        const data = (res as any)?.data ?? (res as any)?.Data ?? res
        setEmployees(Array.isArray(data) ? data : [])
      } catch {
        setEmployees([])
      }
    }
    loadEmployees()
  }, [branchId])

  useEffect(() => {
    if (!isEditing || !id) return
    const load = async () => {
      try {
        setLoading(true)
        const data = await trainingService.getById(Number(id))
        setForm({
          empId: data.empId ?? 0,
          courseTitle: data.courseTitle ?? data.trainingCourseTitle ?? '',
          trainingDonor: data.trainingDonor ?? '',
          trainingYear: data.trainingYear ?? '',
          description: data.description ?? data.trainingDetails ?? '',
        })
      } catch (e: any) {
        toast.error(e?.response?.data?.message ?? e?.message ?? 'Failed to load training.')
        navigate('/trainings')
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [isEditing, id, navigate])

  const employeeOptions = useMemo(
    () =>
      employees.map((e) => {
        const fullName = [e.empFirstNameEn, e.empSecondNameEn, e.empLastNameEn].filter(Boolean).join(' ').trim()
        const label = fullName ? `${e.empCode ?? ''} — ${fullName}`.trim() : e.empCode ?? `Emp ${e.id}`
        return { value: String(e.id ?? 0), label }
      }).filter((o) => o.value !== '0'),
    [employees]
  )

  const handleChange = (patch: Partial<typeof form>) => {
    setForm((prev) => ({ ...prev, ...patch }))
  }

  const handleSave = async () => {
    if (!branchId) {
      toast.error('Please select a branch.')
      return
    }
    if (!form.empId || form.empId === 0) {
      toast.error('Please select an employee.')
      return
    }
    if (!form.courseTitle.trim()) {
      toast.error('Course Title is required.')
      return
    }
    if (!form.trainingDonor.trim()) {
      toast.error('Training Donor is required.')
      return
    }
    const year = typeof form.trainingYear === 'number' ? form.trainingYear : parseInt(String(form.trainingYear), 10)
    if (!Number.isFinite(year) || year < 1900 || year > 2100) {
      toast.error('Year is required and must be valid (e.g. 2020).')
      return
    }
    if (!form.description.trim()) {
      toast.error('Description is required.')
      return
    }

    const payload: TrainingVm = {
      id: isEditing && id ? Number(id) : 0,
      empId: form.empId,
      // Backend field names (Swagger sample)
      trainingCourseTitle: form.courseTitle.trim(),
      trainingDonor: form.trainingDonor.trim(),
      trainingYear: year,
      trainingDetails: form.description.trim(),

      // Frontend field names (compatibility)
      courseTitle: form.courseTitle.trim(),
      description: form.description.trim(),
      branchId,
    }

    try {
      setLoading(true)
      if (isEditing) {
        await trainingService.update(payload)
        await showSuccess('Updated', 'Training updated successfully.')
      } else {
        await trainingService.save(payload)
        await showSuccess('Saved', 'Training saved successfully.')
      }
      navigate('/trainings')
    } catch (e: any) {
      await showError('Save failed', e?.response?.data?.message ?? e?.message ?? 'Failed to save training.')
    } finally {
      setLoading(false)
    }
  }

  if (!branchId) {
    return (
      <div className="space-y-6 p-6">
        <PageHeader title="Training" description="Add or edit training." />
        <Card>
          <CardContent className="p-6 text-center text-muted-foreground">Please select a branch first.</CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title="Training"
        description={isEditing ? 'Edit training' : 'Add new training'}
      />

      <Card>
        <CardHeader>
          <CardTitle>Training</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>Employee *</Label>
              <SearchableSelect
                options={[{ value: '0', label: 'Select employee' }, ...employeeOptions]}
                value={form.empId ? String(form.empId) : '0'}
                onValueChange={(v) => handleChange({ empId: Number(v) || 0 })}
                placeholder="Select employee"
                searchPlaceholder="Search employee..."
                emptyMessage="No employee found"
                disabled={isEditing}
              />
            </div>
            <div className="space-y-2">
              <Label>Course Title *</Label>
              <Input
                value={form.courseTitle}
                onChange={(e) => handleChange({ courseTitle: e.target.value })}
                placeholder="Training course title"
              />
            </div>
            <div className="space-y-2">
              <Label>Training Donor *</Label>
              <Input
                value={form.trainingDonor}
                onChange={(e) => handleChange({ trainingDonor: e.target.value })}
                placeholder="Training donor"
              />
            </div>
            <div className="space-y-2">
              <Label>Year *</Label>
              <Input
                type="number"
                min={1900}
                max={2100}
                value={form.trainingYear === '' ? '' : form.trainingYear}
                onChange={(e) => handleChange({ trainingYear: e.target.value ? parseInt(e.target.value, 10) : '' })}
                placeholder="e.g. 2024"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Description *</Label>
            <Textarea
              value={form.description}
              onChange={(e) => handleChange({ description: e.target.value })}
              placeholder="Description"
              rows={4}
              className="resize-none"
            />
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <Button type="button" variant="outline" onClick={() => navigate('/trainings')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Return
            </Button>
            <Button type="button" onClick={handleSave} disabled={loading}>
              <Save className="w-4 h-4 mr-2" />
              Save
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
